import { type MissionsManager } from "@rescuetablet/missions-client";
import { MissionsContextProvider } from "@rescuetablet/missions-client/react";
import { useEffect, useState, type ReactNode } from "react";
import { useAuthenticated } from "../auth";
import GlobalLoading from "../components/GlobalLoading";
import { log } from "./config";
import { authorizeInOrder } from "./authorize-queue";
import { MissionGroupsProvider } from "./groups";
import { managerPromise } from "./connect";
import { useConnectMissionsStore } from "./store";

export function MissionsProvider({ children }: { children: ReactNode }) {
  const [manager, setManager] = useState<MissionsManager>();
  const [error, setError] = useState<unknown>();
  const { loadCount, missionApiKey } = useAuthenticated();

  useEffect(() => {
    log.info(`Initializing Missions Provider with load count ${loadCount}`);
    setManager(undefined);

    if (!missionApiKey) {
      return setError(new Error("No Mission API key in license."));
    }

    // Results of a superseded run (key or load count changed meanwhile) are ignored
    let current = true;
    managerPromise
      .then(async (mgr) => {
        log.info(`Authorizing Missions Manager with load count ${loadCount}`);
        await authorizeInOrder(mgr, missionApiKey);
        return mgr;
      })
      .then((mgr) => {
        if (!current) return;
        log.info(`Missions Manager initialized with load count ${loadCount}`);
        setManager(mgr);
      })
      .catch((error) => {
        if (!current) return;
        setManager(undefined);
        setError(error);
      });

    return () => {
      current = false;
    };
  }, [missionApiKey, loadCount]);

  if (error) throw error;

  return manager ? (
    <MissionsContextProvider manager={manager}>
      <MissionGroupsProvider>
        <MissionsContent>{children}</MissionsContent>
      </MissionGroupsProvider>
    </MissionsContextProvider>
  ) : (
    <GlobalLoading />
  );
}

function MissionsContent({ children }: { children: ReactNode }) {
  useConnectMissionsStore();

  return <>{children}</>;
}
